import React, { useCallback, useMemo, memo } from 'react'
import useRowHydration from '../../../../../services/community/identity/library/useRowHydration'
import FollowTag from '../../../shared/FollowTag/index'
import PlaylistShareButton from '../../../shared/PlaylistShareButton/index'
import type { PostHogEventSignatures } from '../../../../PostHog/events/index'
import type {
  FollowedPlaylistShellNode, EnrichedPlaylistItem,
} from '../../../../../services/community/types'
import type { PlaylistRowContextValue } from './PlaylistRowContext'
import PlaylistRow from './PlaylistRow'

/**
 * Variant: Following tab row.
 *
 * Per-row hydration via `useRowHydration`. Each row owns its own
 * `useQuery` scope, so sibling rows don't commit when one row's
 * hydration completes.
 *
 * Slot wiring preserves legacy `FollowedPlaylistRowImpl` behavior
 * verbatim (FollowingTab.tsx:124-165): FollowTag goes into the
 * `statusTag` slot, PlaylistShareButton into the `shareButton` slot.
 * The plan's §9.6 column suggesting FollowTag in `nameAction` with no
 * statusTag/shareButton would have changed the user-visible layout —
 * not ratified in the plan's prose, so legacy preserved here.
 *
 * `onClickCapture` on the row frame routes anchor clicks to the
 * `community_playlist_item_played` event with `entry_point: 'following'`.
 * The PostHog hook itself stays in the parent (`ProfileFollowingTab.tsx`)
 * so identity is stable across siblings — `trackPlaylistItemPlayed`
 * arrives as a prop.
 */

export interface FollowedPlaylistRowProps {
  playlist: FollowedPlaylistShellNode
  language: string
  jwt?: string
  authorDisplay: string
  seeMoreLabel: string
  showLessLabel?: string
  onUnfollow: (playlistId: string, playlistName: string) => void
  onRowMount: (playlistId: string, el: HTMLDivElement | null) => void
  trackPlaylistItemPlayed: PostHogEventSignatures['community_playlist_item_played']
}

function FollowedPlaylistRowImpl ({
  playlist,
  language,
  jwt,
  authorDisplay,
  seeMoreLabel,
  showLessLabel,
  onUnfollow,
  onRowMount,
  trackPlaylistItemPlayed,
}: FollowedPlaylistRowProps) {
  const { details, isHydrating } = useRowHydration(playlist, language, { jwt })
  const expectedItemCount = playlist.itemCount ?? 0

  const ctx = useMemo<PlaylistRowContextValue>(() => ({
    playlistId: playlist.id,
    playlistName: playlist.playlistName || '',
    playlistType: playlist.playlistType || undefined,
    items: details.items,
    expectedItemCount,
    totalDuration: details.totalDuration ?? null,
    primaryTopic: details.primaryTopic,
    isHydrating,
    isEditing: false,
  }), [
    playlist.id, playlist.playlistName, playlist.playlistType,
    details.items, details.totalDuration, details.primaryTopic,
    expectedItemCount, isHydrating,
  ])

  // Anchor-click capture → emit playlist_item_played. Mirrors legacy.
  // Tiles render <a href> anchors via ResponsivePlaylistDisplay; matching
  // the href against detailedItems[i].path recovers the contentId + index.
  const handleRowClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null
      if (!target) return
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null
      if (!anchor) return
      const href = anchor.getAttribute('href') || ''
      if (!href) return
      const items = details.items as EnrichedPlaylistItem[]
      const tileIndex = items.findIndex((item) => {
        const path = item.path || ''
        return !!path && (href === path || href.endsWith(path))
      })
      if (tileIndex === -1) return
      const clicked = items[tileIndex]
      const contentId = clicked.contentId != null ? String(clicked.contentId) : ''
      if (!contentId) return
      const contentTitle = clicked.title || undefined
      const creatorDisplayName = playlist.userProfile?.displayName || undefined
      trackPlaylistItemPlayed(
        contentId,
        'following',
        playlist.id,
        tileIndex,
        'followed',
        undefined,
        contentTitle,
        creatorDisplayName,
      )
    },
    [details.items, playlist.id, playlist.userProfile, trackPlaylistItemPlayed],
  )

  const handleUnfollowClick = useCallback(
    () => onUnfollow(playlist.id, playlist.playlistName || ''),
    [onUnfollow, playlist.id, playlist.playlistName],
  )

  const handleRefMount = useCallback(
    (el: HTMLDivElement | null) => onRowMount(playlist.id, el),
    [onRowMount, playlist.id],
  )

  // Memoise the header slots so `CommunityPlaylistRowHeader`'s `React.memo`
  // shallow-equal passes across hydration-driven row re-renders. Without
  // this, the inline `<FollowTag>` / `<PlaylistShareButton>` elements have
  // fresh identity per render, defeating the downstream memo and re-firing
  // `usePreloadImages` inside `PlaylistShareButton`. See `MyPlaylistRow.tsx`
  // for the canonical rationale.
  const headerStatusTag = useMemo(() => (
    <FollowTag
      isFollowing
      followerCount={playlist.followerCount || 0}
      onUnfollow={handleUnfollowClick}
    />
  ), [playlist.followerCount, handleUnfollowClick])

  const headerShareButton = useMemo(() => (
    <PlaylistShareButton
      playlistId={playlist.id}
      playlistName={playlist.playlistName || ''}
      description={playlist.description}
      items={details.items as EnrichedPlaylistItem[]}
    />
  ), [playlist.id, playlist.playlistName, playlist.description, details.items])

  return (
    <PlaylistRow.Provider value={ctx}>
      <PlaylistRow.Frame
        className="playlist-tab-container__playlist-row"
        innerRef={handleRefMount}
        onClickCapture={handleRowClickCapture}
      >
        <PlaylistRow.Header
          authorDisplay={authorDisplay}
          authorUid={playlist.userProfile?.uuid || null}
          statusTag={headerStatusTag}
          shareButton={headerShareButton}
        />
        <PlaylistRow.Body
          seeMoreLabel={seeMoreLabel}
          showLessLabel={showLessLabel}
        />
      </PlaylistRow.Frame>
    </PlaylistRow.Provider>
  )
}

const FollowedPlaylistRow = memo(FollowedPlaylistRowImpl)
FollowedPlaylistRow.displayName = 'FollowedPlaylistRow'
export default FollowedPlaylistRow
